import moment from 'moment';
import { styledDefaultColDef, createAvatarNameRenderer } from '../../../extras/cellRenderers';

import StatusDropdown from './StatusDropdown';
import EvaluatorDropdown from './EvaluatorDropdown';
import SubmittedSolutionModal from './SubmittedSolutionDetails';
import ViewFeedbackModal from './ViewFeedbackModal';
import IncreaseAttemptModal from './IncreaseAttemptModal';
import AdminFeedbackModal from './AdminFeedbackModal';

const NameGetter = createAvatarNameRenderer(
  (p) => p?.data?.Student?.firstName,
  (p) => p?.data?.Student?.lastName
);

const getFullName = (params) => {
  return params?.data?.Student?.firstName
    ? params?.data?.Student?.firstName + ' ' + params?.data?.Student?.lastName
    : '';
};

export const columnDefs = (allAssignmentList) => [
  {
    headerName: 'Name',
    field: 'name',
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      suppressAndOrCondition: true,
    },

    width: 200,
    cellRenderer: NameGetter,
    valueGetter: (params) => {
      return getFullName(params);
    },
  },

  {
    headerName: 'Email',
    field: 'email',
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      suppressAndOrCondition: true,
    },
    width: 250,
    valueGetter: (params) => {
      return params?.data?.Student?.email;
    },
    cellRenderer: (params) => {
      return (
        <div className='flex items-center h-full'>
          {params?.data?.Student?.email}
        </div>
      );
    },
  },

  {
    headerName: 'Submission Date',
    field: 'submittedAt',
    sortable: true,

    filter: 'agDateColumnFilter',
    filterParams: {
      suppressAndOrCondition: true,
      filterOptions: ['equals', 'greaterThan', 'inRange'],
    },

    valueGetter: (params) => {
      return moment(params?.data?.submittedAt).format('DD MMM YYYY');
    },
    cellRenderer: function SubmissionDateGetter(params) {
      return (
        <div className='flex items-center h-full'>
          {moment(params?.data?.submittedAt).format('DD MMM YYYY')}
        </div>
      );
    },
    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => valueA - valueB,
  },

  {
    headerName: 'Evaluation Date',
    field: 'evaluatedAt',
    sortable: true,

    filter: 'agDateColumnFilter',
    filterParams: {
      suppressAndOrCondition: true,
      filterOptions: ['equals', 'greaterThan', 'inRange'],
    },

    valueGetter: (params) => {
      return params?.data?.evaluatedAt
        ? moment(params?.data?.evaluatedAt).format('DD MMM YYYY')
        : '-';
    },
    cellRenderer: function EvaluationDateGetter(params) {
      return (
        <div className='flex items-center h-full'>
          {params?.data?.evaluatedAt
            ? moment(params?.data?.evaluatedAt).format('DD MMM YYYY')
            : '-'}
        </div>
      );
    },
    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => valueA - valueB,
  },

  {
    headerName: 'Plan Type',
    field: 'plan_type',
    filter: false,
    sortable: false,
    width: 200,
    valueGetter: (params) => {
      return params?.data?.UserSubscription?.planType;
    },
    cellRenderer: function PlanGetter(params) {
      return (
        <div className='flex items-center h-full'>
          {params?.data?.UserSubscription?.planType}
        </div>
      );
    },
  },

  {
    headerName: 'Cohort',
    field: 'cohort',
    sortable: false,

    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      suppressAndOrCondition: true,
    },

    width: 200,
    valueGetter: (params) => {
      return params?.data?.UserSubscription?.cohortName;
    },
    cellRenderer: function CohortGetter(params) {
      return (
        <div className='flex items-center h-full'>
          {params?.data?.UserSubscription?.cohortName}
        </div>
      );
    },
  },

  {
    headerName: 'AlmaX Cohort',
    field: 'almaXCohort',
    sortable: false,

    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      suppressAndOrCondition: true,
    },

    width: 200,
    valueGetter: (params) => {
      return params?.data?.UserSubscription?.almaxCohortName;
    },
    cellRenderer: function CohortGetter(params) {
      return (
        <div className='flex items-center h-full'>
          {params?.data?.UserSubscription?.almaxCohortName}
        </div>
      );
    },
  },

  {
    headerName: 'Assignment',
    field: 'assignment',
    sortable: false,

    filter: 'agSetColumnFilter',
    filterParams: {
      values: allAssignmentList?.map((assignment) => assignment.name),
      cellHeight: 50,
      cellRenderer: (params) => {
        return (
          <div className='flex items-center w-52 whitespace-pre-wrap'>
            {params?.value}
          </div>
        );
      },
    },

    width: 200,
    valueGetter: (params) => {
      return params?.data?.assignment?.attributes?.name;
    },
    cellRenderer: function AssignmentGetter(params) {
      return (
        <div>
          <SubmittedSolutionModal params={params} />
        </div>
      );
    },
  },

  {
    headerName: 'Admin Status',
    field: 'adminStatus',
    sortable: false,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['pending', 'accepted', 'not_needed'],
    },
    width: 250,

    cellRenderer: function StatusGetter(params) {
      return (
        <div className='flex items-center h-full p-6'>
          <StatusDropdown params={params} />
        </div>
      );
    },
  },

  {
    headerName: 'Evaluation Status',
    field: 'evaluationStatus',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['pending', 'completed', 'rejected'],
    },
    sortable: false,
    width: 200,
    cellRenderer: function getEvaluationStatus(params) {
      return (
        <div className='flex items-center h-full p-6'>
          {params?.data?.evaluationStatus || '-'}
        </div>
      );
    },
  },

  {
    headerName: 'Attempts',
    field: 'attempts',
    sortable: false,
    filter: false,
    width: 200,
    cellRenderer: function getEvaluationStatus(params) {
      return (
        <div className='flex items-center h-full p-6'>
          <IncreaseAttemptModal params={params} />
        </div>
      );
    },
  },

  {
    headerName: 'Evaluator',
    field: 'evaluator',
    sortable: false,

    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: [
        'contains',
        {
          displayKey: 'noEvaluator',
          displayName: 'No Evaluator',
          predicate: (_, cellValue) => !cellValue,
          numberOfInputs: 0,
        },
      ],
      suppressAndOrCondition: true,
    },

    width: 250,
    cellRenderer: function EvaluatorGetter(params) {
      return (
        <div className='flex items-center h-full p-6'>
          <EvaluatorDropdown params={params} />
        </div>
      );
    },
  },

  {
    headerName: 'Evaluator Email',
    field: 'evaluatorEmail',
    sortable: false,

    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      suppressAndOrCondition: true,
    },

    hide: true,
    width: 220,
    valueGetter: (params) => {
      return params?.data?.Evaluator?.email;
    },
    cellRenderer: function (params) {
      return (
        <div className='flex items-center h-full '>
          {params.data?.Evaluator?.email}
        </div>
      );
    },
  },

  {
    headerName: 'Score',
    field: 'score',
    sortable: false,
    filter: false,
    width: 200,

    cellRenderer: function ScoreGetter(params) {
      return (
        <div className='flex items-center h-full p-6'>
          <AdminFeedbackModal params={params} />
        </div>
      );
    },
  },

  {
    headerName: 'View Feedback',
    field: 'view_feedback',
    sortable: false,
    filter: false,
    width: 200,

    cellRenderer: function AssignmentGetter(params) {
      return (
        <div>
          <ViewFeedbackModal
            goalType={params?.data?.goalType}
            assignmentId={params?.data?.assignmentId}
            userId={params?.data?.userId}
            assignmentImgUrl={
              params.data?.assignment?.attributes?.icon?.data?.attributes?.url
            }
            assignmentName={params.data?.assignment?.attributes?.name}
          />
        </div>
      );
    },
  },
];

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
};
